import { DimensionNode } from '@bayer-int/portletce-shared';
import * as t from 'io-ts';
import { DateTime } from 'luxon';

import { toISODateString } from 'common/utils';

import { hasKey, isNotNullable, isString, isBoolean, toArray, hasKeys } from '../common/utils';
import { SCALE_OPTIONS } from './settings/settingDefinitions/scaleSetting';

export const DateTypeC = t.union([
    t.literal('date'),
    t.literal('datetime'),
    t.literal('year'),
    t.literal('month'),
    t.literal('time'),
    t.literal('week'),
]);
export type DateType = t.TypeOf<typeof DateTypeC>;

const HierarchyFilterNodeC = t.intersection([
    t.type({
        // it's basically: export type HierarchyFilterNode = Omit<HierarchyNode, 'children'>; but IO-TS doesn't support self referring codecs
        identifier: t.string,
        key: t.string,
        value: t.string,
        characteristicId: t.string,
        internalKey: t.string,
        basedOnInternalKey: t.string,
        basedOnCharacteristicId: t.string,
        hierarchyId: t.string,
        keyPathToRoot: t.array(t.string),
        identifierPathToRoot: t.array(t.string),
        valuePathToRoot: t.array(t.string),
    }),
    t.partial({
        nodeName: t.union([t.string, t.undefined]),
        hierarchyVariantId: t.union([t.string, t.undefined]),
        /**
         * Depending on usage scenario, we may want to modify how the filter node should be handled.
         * For example, for certain portlets we may want to keep the node inside the filter (to e.g. ensure consistency with hierarchy selector)
         * but ignore the node when building bw query.
         *
         * Introduced in 3167: Hierarchy filtering - don't autoselect upstream
         */
        effect: t.union([
            // default
            t.literal('active'),
            // should not be used when building queries, etc
            t.literal('disabled'),
            // relevant for virtual hierarchies, which contain selected nodes and their ancestors
            // if 'upstream-disable' is set on a node, all its ancestors should have 'effect: disabled'
            t.literal('upstream-disable'),
        ]),
    }),
]);

export type HierarchyFilterNode = t.TypeOf<typeof HierarchyFilterNodeC>;
export const HierarchyFilterNode = { codec: HierarchyFilterNodeC };

const FilterTypeC = t.type({
    DIMENSION: t.literal('DIMENSION'),
    DATE: t.literal('DATE'),
    DATE_RANGE: t.literal('DATE_RANGE'),
    HIERARCHY: t.literal('HIERARCHY'),
    SCALE: t.literal('SCALE'),
    SMART_VIEW_TAG: t.literal('SMART_VIEW_TAG'),
    ROLE: t.literal('ROLE'),
    NUMBER: t.literal('NUMBER'),
});

export type FilterType = t.TypeOf<typeof FilterTypeC>;
export const FilterType = { codec: FilterTypeC };

const FilterValueTypeC = t.union([
    t.literal('DIMENSION'),
    t.literal('DATE'),
    t.literal('DATE_RANGE'),
    t.literal('HIERARCHY'),
    t.literal('SCALE'),
    t.literal('SMART_VIEW_TAG'),
    t.literal('ROLE'),
    t.literal('NUMBER'),
]);

export type FilterValueType = t.TypeOf<typeof FilterValueTypeC>;
export const FilterValueType = { codec: FilterValueTypeC };

const DimensionNodeInputCodec = t.intersection([
    t.type({ key: t.string }),
    t.partial({
        value: t.string,
        characteristicId: t.string,
        internalKey: t.string,
        hierarchyId: t.string,
        basedOnInternalKey: t.string,
        basedOnCharacteristicId: t.string,
        keyPathToRoot: t.array(t.string),
        identifier: t.string,
        identifierPathToRoot: t.array(t.string),
    }),
]);
const HierarchyNodeInputCodec = t.intersection([
    t.type({ key: t.string }),
    t.partial({
        value: t.string,
        keyPathToRoot: t.array(t.string),
        identifier: t.string,
        identifierPathToRoot: t.array(t.string),
        characteristicId: t.string,
        internalKey: t.string,
        basedOnInternalKey: t.string,
        basedOnCharacteristicId: t.string,
        hierarchyId: t.string,
        hierarchyVariantId: t.string,
        nodeName: t.string,
    }),
]);
const HierarchyInputCodec = t.type({
    hierarchyInternalKeys: t.array(t.string),
    values: t.union([HierarchyNodeInputCodec, t.array(HierarchyNodeInputCodec)]),
});
const ScaleInputCodec = t.intersection([
    t.type({ key: t.union([t.string, t.number]) }),
    t.partial({ label: t.string }),
]);
const RoleInputCode = t.intersection([t.type({ key: t.string }), t.partial({ label: t.string })]);

export const FilterValueInputC = t.type({
    type: FilterValueType.codec,
    value: t.union([
        t.string,
        t.tuple([t.string, t.string]),
        t.array(t.string),
        ScaleInputCodec,
        DimensionNodeInputCodec,
        t.array(DimensionNodeInputCodec),
        HierarchyInputCodec,
        RoleInputCode,
        t.number,
    ]),
});

const FilterValueC = t.union([
    t.type({
        type: FilterTypeC.props.DIMENSION,
        value: t.array(DimensionNode.codec),
        exclude: t.boolean,
    }),
    t.type({
        type: FilterTypeC.props.DATE,
        value: t.string,
        dateType: DateTypeC,
    }),
    t.type({
        type: FilterTypeC.props.DATE_RANGE,
        value: t.tuple([t.string, t.string]),
        dateType: DateTypeC,
    }),
    t.type({
        type: FilterTypeC.props.SCALE,
        value: t.type({ key: t.string, label: t.string }),
    }),
    t.type({
        type: FilterTypeC.props.HIERARCHY,
        value: t.type({
            hierarchyInternalKeys: t.array(t.string),
            values: t.array(HierarchyFilterNode.codec),
        }),
        exclude: t.boolean,
    }),
    t.type({
        type: FilterTypeC.props.SMART_VIEW_TAG,
        viewTag: t.string,
    }),
    t.type({
        type: FilterTypeC.props.ROLE,
        value: t.type({ key: t.string, label: t.string }),
    }),
    t.type({
        type: FilterTypeC.props.NUMBER,
        value: t.number,
    }),
]);

export type FilterValue = t.TypeOf<typeof FilterValueC>;

export type TypedFilterValue<T extends FilterValueType> = FilterValue & { type: T };

export const FilterValue = {
    codec: FilterValueC,
    DIMENSION: (
        val: Omit<TypedFilterValue<'DIMENSION'>, 'type'>,
    ): TypedFilterValue<'DIMENSION'> => ({ ...val, type: 'DIMENSION' }),

    DATE: (val: Omit<TypedFilterValue<'DATE'>, 'type'>): TypedFilterValue<'DATE'> => ({
        ...val,
        type: 'DATE',
    }),

    DATE_RANGE: (
        val: Omit<TypedFilterValue<'DATE_RANGE'>, 'type'>,
    ): TypedFilterValue<'DATE_RANGE'> => ({ ...val, type: 'DATE_RANGE' }),

    SCALE: (val: Omit<TypedFilterValue<'SCALE'>, 'type'>): TypedFilterValue<'SCALE'> => ({
        ...val,
        type: 'SCALE',
    }),

    SMART_VIEW_TAG: (
        val: Omit<TypedFilterValue<'SMART_VIEW_TAG'>, 'type'>,
    ): TypedFilterValue<'SMART_VIEW_TAG'> => ({
        ...val,
        type: 'SMART_VIEW_TAG',
    }),

    HIERARCHY: (
        val: Omit<TypedFilterValue<'HIERARCHY'>, 'type'>,
    ): TypedFilterValue<'HIERARCHY'> => ({ ...val, type: 'HIERARCHY' }),

    ROLE: (val: Omit<TypedFilterValue<'ROLE'>, 'type'>): TypedFilterValue<'ROLE'> => ({
        ...val,
        type: 'ROLE',
    }),

    NUMBER: (val: Omit<TypedFilterValue<'NUMBER'>, 'type'>): TypedFilterValue<'NUMBER'> => ({
        ...val,
        type: 'NUMBER',
    }),

    is: {
        DIMENSION: (a: FilterValue): a is TypedFilterValue<'DIMENSION'> => a.type === 'DIMENSION',
        DATE: (a: FilterValue): a is TypedFilterValue<'DATE'> => a.type === 'DATE',
        DATE_RANGE: (a: FilterValue): a is TypedFilterValue<'DATE_RANGE'> =>
            a.type === 'DATE_RANGE',
        SCALE: (a: FilterValue): a is TypedFilterValue<'SCALE'> => a.type === 'SCALE',
        HIERARCHY: (a: FilterValue): a is TypedFilterValue<'HIERARCHY'> => a.type === 'HIERARCHY',
        SMART_VIEW_TAG: (a: FilterValue): a is TypedFilterValue<'SMART_VIEW_TAG'> =>
            a.type === 'SMART_VIEW_TAG',
        ROLE: (a: FilterValue): a is TypedFilterValue<'ROLE'> => a.type === 'ROLE',
        NUMBER: (a: FilterValue): a is TypedFilterValue<'NUMBER'> => a.type === 'NUMBER',
    },

    match: <T>(
        a: FilterValue,
        matchers: { [Key in FilterValueType]: (a: TypedFilterValue<Key>) => T },
    ): T => {
        const fn = matchers[a.type] as (a: FilterValue) => T;
        return fn(a);
    },

    toString: (a: FilterValue): string =>
        FilterValue.match(a, {
            DIMENSION: ({ value }) => value.map(({ key }) => key).join(','),
            DATE: ({ value }) => value,
            DATE_RANGE: ({ value: [from, to] }) => `${from}-${to}`,
            SCALE: ({ value }) => value.key,
            // We want to display only applied values, so
            // those with effect 'disabled' have to be filtered out.
            // It depends on 'filterUpstream' setting from ROI5-3167.
            HIERARCHY: ({ value }) =>
                value.values
                    .filter(v => v.effect !== 'disabled')
                    .map(({ key }) => key)
                    .join(','),
            SMART_VIEW_TAG: ({ viewTag }) => viewTag,
            ROLE: ({ value }) => value.key,
            NUMBER: ({ value }) => value.toLocaleString(),
        }),

    tryParse: (a: unknown): FilterValue | null => {
        return (
            FilterValue.tryParseDate(a) ||
            FilterValue.tryParseScale(a) ||
            FilterValue.tryParseRole(a) ||
            FilterValue.tryParseDateRange(a) ||
            FilterValue.tryParseDimension(a) ||
            FilterValue.tryParseHierarchy(a) ||
            FilterValue.tryParseSmartViewTag(a) ||
            FilterValue.tryParseNumber(a)
        );
    },

    tryParseDate,
    tryParseDateRange,
    tryParseScale,
    tryParseRole,
    tryParseDimension,
    tryParseHierarchy,
    tryParseSmartViewTag,
    tryParseNumber,
};

function isDateType(a: unknown): a is DateType {
    return DateTypeC.is(a);
}

function tryParseDate(a: unknown): FilterValue | null {
    if (hasKeys(a, 'type', 'value') && a.type === 'DATE' && isString(a.value)) {
        return FilterValue.DATE({
            value: toISODateString(a.value) || DateTime.utc().toISO(),
            dateType: hasKey(a, 'dateType') && isDateType(a.dateType) ? a.dateType : 'date',
        });
    }

    const validIsoString = toISODateString(a);

    return validIsoString ? FilterValue.DATE({ value: validIsoString, dateType: 'date' }) : null;
}

function tryParseDateRange(a: unknown): FilterValue | null {
    const dates = hasKeys(a, 'type', 'value') && a.type === 'DATE_RANGE' ? a.value : a;
    const dateType = hasKeys(a, 'dateType') && isDateType(a.dateType) ? a.dateType : 'date';

    if (!Array.isArray(dates) || dates.length !== 2) return null;

    const [from, to] = dates.map(toISODateString);

    return from && to ? FilterValue.DATE_RANGE({ value: [from, to], dateType }) : null;
}

function tryParseDimension(a: unknown): FilterValue | null {
    const hasExplicitDimensionType = hasKey(a, 'type') && a.type === 'DIMENSION';
    const values = hasKeys(a, 'type', 'value') && a.type === 'DIMENSION' ? a.value : a;
    const exclude = hasKeys(a, 'exclude') && isBoolean(a.exclude) ? a.exclude : false;

    const dimensionNodeDefaults: Omit<DimensionNode, 'key' | 'value'> = {
        characteristicId: '',
        internalKey: '',
        identifier: '',
        basedOnCharacteristicId: '',
        basedOnInternalKey: '',
    };

    const dimensionValues: DimensionNode[] = toArray(values)
        .map(value => {
            return t.string.is(value)
                ? { ...dimensionNodeDefaults, key: value, value }
                : DimensionNodeInputCodec.is(value)
                ? { ...dimensionNodeDefaults, value: value.key, ...value }
                : null;
        })
        .filter(isNotNullable);

    return hasExplicitDimensionType || dimensionValues.length
        ? FilterValue.DIMENSION({ value: dimensionValues, exclude })
        : null;
}

function tryParseScale(a: unknown): FilterValue | null {
    const value = hasKeys(a, 'type', 'value') && a.type === 'SCALE' ? a.value : a;

    const getLabel = (value: string | number): string =>
        SCALE_OPTIONS.find(({ key }) => key === `${value}`)?.label || `${value}`;

    const scaleValue =
        t.string.is(value) || t.number.is(value)
            ? {
                  key: `${value}`,
                  label: getLabel(value),
              }
            : ScaleInputCodec.is(value)
            ? { key: `${value.key}`, label: value.label ?? getLabel(value.key) }
            : null;

    return hasKeys(a, 'type', 'value') && a.type === 'SCALE' && scaleValue
        ? FilterValue.SCALE({ value: scaleValue })
        : null;
}

function tryParseNumber(a: unknown): FilterValue | null {
    const value = hasKeys(a, 'type', 'value') && a.type === 'NUMBER' ? a.value : a;

    return t.number.is(value) ? FilterValue.NUMBER({ value }) : null;
}

function tryParseHierarchy(a: unknown): FilterValue | null {
    const exclude = hasKeys(a, 'exclude') && isBoolean(a.exclude) ? a.exclude : false;

    const hierarchyInputCodec = t.intersection([
        t.type({ values: t.unknown }),
        t.partial({
            hierarchyInternalKeys: t.array(t.string),
        }),
    ]);

    const hierarchyFilterNodeDefault = {
        characteristicId: '',
        internalKey: '',
        basedOnInternalKey: '',
        basedOnCharacteristicId: '',
        hierarchyId: '',
        identifierPathToRoot: [] as string[],
        keyPathToRoot: [] as string[],
        valuePathToRoot: [] as string[],
    };

    const [hierarchyInternalKeys, values] =
        hasKeys(a, 'type', 'value') && a.type === 'HIERARCHY'
            ? hierarchyInputCodec.is(a.value)
                ? [a.value.hierarchyInternalKeys, a.value.values]
                : [null, null]
            : [null, null];

    const hierarchyValues: HierarchyFilterNode[] = toArray(values)
        .map(value => {
            return t.string.is(value)
                ? { ...hierarchyFilterNodeDefault, key: value, value, identifier: value }
                : HierarchyNodeInputCodec.is(value)
                ? {
                      ...hierarchyFilterNodeDefault,
                      identifier: value.key,
                      value: value.key,

                      ...value,
                  }
                : null;
        })
        .filter(isNotNullable);

    return (hasKey(a, 'type') && a.type === 'HIERARCHY') || hierarchyValues.length
        ? FilterValue.HIERARCHY({
              value: {
                  hierarchyInternalKeys: hierarchyInternalKeys ?? [],
                  values: hierarchyValues,
              },
              exclude,
          })
        : null;
}

function tryParseSmartViewTag(a: unknown): FilterValue | null {
    return t.type({ viewTag: t.string }).is(a)
        ? FilterValue.SMART_VIEW_TAG({ viewTag: a.viewTag })
        : null;
}

function tryParseRole(a: unknown): FilterValue | null {
    const value = hasKeys(a, 'type', 'value') && a.type === 'ROLE' ? a.value : a;

    const roleValue = t.string.is(value)
        ? { key: value, label: value }
        : RoleInputCode.is(value)
        ? { key: value.key, label: value.label ?? value.key }
        : null;

    return hasKeys(a, 'type', 'value') && a.type === 'ROLE' && roleValue
        ? FilterValue.ROLE({ value: roleValue })
        : null;
}
